import React, { FC, useRef, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { withRouter, BrowserRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { useBoolean } from '@fluentui/react-hooks';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';

import {
  IApplicationContext, ServiceContext, ExtensionsRegistrationClient,
  ApplicationContext, IUser
} from '@msx/platform-services';
import {
  getExtensionsLoadingStatus, fetchExtensionsRegistration,
  getExtensionsRegistration,
  getCurrentLocale, setCurrentLocale,
  getCurrentTheme, setCurrentTheme,
  getCurrentAppState,
} from '../../store';


import {
  fetchUserProfileBegin, fetchUserProfileSuccess,
  getUserProfileLoadingStatus,
  getUserDashboardTiles,
} from '../../../app/store';


import { PageError } from '../'
import { AppHeader } from '../../../app/components'
import { appConfig } from '../../../app/App.config';
import { getStyles } from './Shell.styles';
import { BusyIndicator } from '../';
import { messages } from './Shell.messages';
import { getThemeByName } from '../../utils';
import { Signin, } from '../../../app/pages';
import { ShellProps } from './Shell.types';
import { DataServiceContext } from '../../../app/context';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const getClassNames = classNamesFunction<any, any>();
let classes: any;
const defaultUser: IUser = {
  id: '',
  name: '',
  email: '',
}

const ShellComponent: FC<ShellProps> = (props) => {
  const { intl } = props;
  const reduxDispatch = useDispatch();
  const currentAppState = useSelector(getCurrentAppState);
  const currentTheme = useSelector(getCurrentTheme);
  const userDashboardTiles = useSelector(getUserDashboardTiles);
  const userProfileLoadingStatus = useSelector(getUserProfileLoadingStatus);
  const extensionsLoadingStatus = useSelector(getExtensionsLoadingStatus);
  const [showFabricComponent, setShowFabricComponent] = useState(false);
  const extensionsRegistration = useSelector(getExtensionsRegistration);
  const extensionsRegistrationClient = new ExtensionsRegistrationClient(extensionsRegistration);
  const [isStartupError, { setTrue: activateStartupError, setFalse: dismissStartupError }] = useBoolean(false);
  const [startupError, setStartupError] = useState('');
  const currentLocale = useSelector(getCurrentLocale);
  const currentLocaleRef = useRef(currentLocale);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const isUserLoggedInRef = useRef(isUserLoggedIn);
  const { authClient } = useContext(ServiceContext);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [currentUser, setCurentUser] = useState(defaultUser);
  const { dataService } = useContext(DataServiceContext);

  // Refs for skip link and main content
  const mainContentRef = useRef<HTMLElement>(null);

  classes = getClassNames(getStyles, currentTheme);

  useEffect(() => {
    //MOUNT
    loadStartupData();
    window.removeEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);
    //UNMOUNT
    return () => {
      window.removeEventListener('resize', handleResize);
    }
    // eslint-disable-next-line
  }, [])

  // Skip link event handlers
  const handleSkipLinkKeyDown = useCallback((event: React.KeyboardEvent<HTMLAnchorElement>) => {
    // Only prevent default for Enter and Space keys, allow Tab to work normally
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (mainContentRef.current) {
        mainContentRef.current.focus();
      }
    }
  }, [mainContentRef]);

  const handleResize = useCallback((): void => {
    // do something
  }, []);

  const loadStartupData = useCallback(async () => {
    const isLoggedIn = await authClient.isLoggedIn();
    setIsUserLoggedIn(isLoggedIn);
    if (isLoggedIn) {
      authClient.getUser().then(user => {
        setCurentUser(user);
      });
    }
    if (!isLoggedIn) {
      return;
    }
    const Window = window as any;
    if (Window.hasStartupDataLoaded === true || Window.hasStartupDataInprogress === true) {
      return;
    }

    Window.hasStartupDataInprogress = true;
    console.log('Loading startup data...')
    try {
      dismissStartupError();


      // load user profile
      if (appConfig.enableUserSettingsApi) {
        reduxDispatch(fetchUserProfileBegin());
        const userProfile = await dataService.GetUserProfile();
        reduxDispatch(fetchUserProfileSuccess(userProfile));
        const locale = userProfile.userPreference.locale;
        if (locale !== currentLocaleRef.current) {
          reduxDispatch(setCurrentLocale(locale));
        }
        const themeName = userProfile.userPreference.theme;
        if (currentTheme.name !== themeName) {
          const newTheme = getThemeByName(themeName);
          reduxDispatch(setCurrentTheme(newTheme));
        }
      }

      // load extension registraton      
      if (appConfig.registrationConfig.active) {
        reduxDispatch(fetchExtensionsRegistration(dataService, []));
      }
      Window.hasStartupDataLoaded = true;
      Window.hasStartupDataInprogress = false;
    } catch (error) {
      Window.hasStartupDataInprogress = false;
      setStartupError(JSON.stringify(error));
      activateStartupError();
    }
  }, [authClient, currentTheme, dataService, reduxDispatch, activateStartupError, dismissStartupError, setIsUserLoggedIn, setCurentUser,
    appConfig, currentLocaleRef, getThemeByName, setStartupError
  ]);

  const handleHeaderButtonAccess = useCallback(() => {
    // show/hide feedback button
    let showFeedback = currentAppState.isReady && isUserLoggedIn;
    if (!appConfig.ocvFeedbackConfig.active) {
      showFeedback = false;
    }
    let el: any = document.querySelector('#coher-header-ocv-button-id');
    if (el) el.style.display = showFeedback ? '' : 'none';

    el = document.querySelector(`a[aria-label="${appConfig.appName}"]`);
    if (el) {
      el.style.color = 'white !important';
    }

  }, [currentAppState, isUserLoggedIn, appConfig]);

  const handleFabricVisibilityChange = useCallback((isVisible: boolean) => {
    setShowFabricComponent(isVisible);
  }, [setShowFabricComponent]);

  const handleLocaleChange = useCallback((locale) => {
    reduxDispatch(setCurrentLocale(locale));
  }, [reduxDispatch]);

  const getDisplayLocales = useCallback(() => {
    let result = [];
    if (!appConfig.languageConfig.active) {
      return result;
    }
    const locales = appConfig.languageConfig.locales;
    Object.keys(locales).forEach(function (key, idx) {
      result.push(
        {
          key: key,
          text: locales[key]
        });
    });
    return result;
  }, [appConfig?.languageConfig?.active, appConfig?.languageConfig?.locales]);

  const renderAppHeader = useCallback(() => {
    return (
      <AppHeader
        showFabricComponent={showFabricComponent}
        onFabricVisibilityChanged={handleFabricVisibilityChange}
        locales={getDisplayLocales()}
        dispatchUpdateLocaleAction={handleLocaleChange}
        isAppReady={currentAppState.isReady}
        theme={currentTheme}
        appName={appConfig.appName}
        isUserLoggedIn={isUserLoggedIn}
        user={currentUser}
        pageList={extensionsRegistrationClient.getExtensionsPages()} 
        intl={intl}
        appNameTooltip={appConfig.appNameTooltip}     
         />
    );
  }, [showFabricComponent, handleFabricVisibilityChange, currentAppState, isUserLoggedIn, currentUser, currentTheme, appConfig, getDisplayLocales, handleLocaleChange,
    extensionsRegistrationClient, intl
  ]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const renderRoutes = () => {
    const Window = window as any;

    if (Window.hasStartupDataInprogress === true) {
      return null;
    }
    if (isStartupError) {
      return <PageError error={startupError} />
    }
    if (appConfig.enableUserSettingsApi && userProfileLoadingStatus) {
      return <BusyIndicator message={intl.formatMessage(messages.loadingUserProfile)} />
    }
    if (appConfig.registrationConfig.active) {
      if (extensionsLoadingStatus) {
        return <BusyIndicator message={intl.formatMessage(messages.loadingExtensionRegistraton)} />
      }
    }
    return props.onRenderRoutes(extensionsRegistrationClient.getExtensionsPages());
  }

  const renderNotifyContainer = () => {
    const isResourcePanelOpen = document.getElementById("resource-panel-component");
    if (isResourcePanelOpen) {
      return null;
    };
    return (
      <ToastContainer style={{ top: "48px" }}
        role="alert"
        toastClassName='default-toast'
        position={toast.POSITION.TOP_RIGHT}
        autoClose={5000}
        hideProgressBar={true}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    );
  }

  const newAppState = useMemo(() => ({
      ...currentAppState,
      user: currentUser,
      appConfig,
      locale: currentLocale,
      theme: currentTheme,
      isUserLoggedIn: isUserLoggedIn,
      isNavCollapsed: isNavCollapsed,
      themeCustomizations: () => {},
    }), [currentAppState, currentUser, currentLocale, currentTheme, isUserLoggedIn, isNavCollapsed, appConfig]);

    const contextValue: IApplicationContext = useMemo(() => ({
      extensionsRegistrationClient: extensionsRegistrationClient,
      appState: newAppState,
      userDashboardTiles: userDashboardTiles,
    }), [extensionsRegistrationClient, newAppState, userDashboardTiles]);

  const renderMain = (): JSX.Element => {
    currentLocaleRef.current = currentLocale;
    isUserLoggedInRef.current = isUserLoggedIn;
    
    return (
      <ApplicationContext.Provider value={contextValue}>
        <>
          {!isUserLoggedIn ? <Signin isUserLoggedIn={isUserLoggedIn} /> : (
            <>
              {renderNotifyContainer()}
              {renderApp()}
            </>
          )}
        </>
      </ApplicationContext.Provider>
    )
  }

  const renderApp = (): JSX.Element => {
    handleHeaderButtonAccess();
    //const themeCustomizations = getThemeCustomizations();
    return (
      // <ThemeProvider theme={themeCustomizations}>
      <>
        <a 
          href="#main" 
          className={classes.skipToMainContentLink}
          onKeyDown={handleSkipLinkKeyDown}
        >
          Skip to main content
        </a>
        <BrowserRouter>
          <>
            {renderAppHeader()}
            <main ref={mainContentRef} id='main' tabIndex={-1} role='main' className={classes.main}>
              {
              renderRoutes()
              }
            </main>
          </>
        </BrowserRouter>
        <div role="alert" id="announce" aria-live="polite" aria-relevant="additions text"></div>
        </>
      // </ThemeProvider>
    )
  }

  return renderMain();
};

export const Shell = withRouter(injectIntl(ShellComponent));
